import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../styles/AuthStyles.module.css';
import useAnalytics from '../../hooks/useAnalytics';
import userService from '../../services/UserService';
import patientService from '../../services/PatientService';
import { startLoginTour } from '../../tours/loginTour';
import '../../tours/tourStyles.css';

const EmailScreen = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [isPatientMode, setIsPatientMode] = useState(true); // Default to patient mode
  const navigate = useNavigate();
  const location = useLocation();
  const { trackLogin } = useAnalytics();

  // Handle email invitation links with query parameters (keep for compatibility)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const invitedParam = params.get('invited');
    const emailParam = params.get('email');

    if (invitedParam === 'true' && emailParam) {
      // If user arrived via invitation link, go directly to dashboard
      navigate('/dashboard/patient');
    }
  }, [location, navigate]);

  // Start tour when in patient mode (first-time users only)
  useEffect(() => {
    if (isPatientMode) {
      startLoginTour();
    }
  }, [isPatientMode]);

  // Handle OTP-style input for access code
  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    
    // Update the full access code
    const fullCode = newDigits.join('');
    setAccessCode(fullCode);
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');
    
    const newDigits = ['', '', '', '', '', ''];
    digits.forEach((digit, i) => {
      if (i < 6) newDigits[i] = digit;
    });
    
    setCodeDigits(newDigits);
    setAccessCode(newDigits.join(''));
    
    // Focus the last filled input or the first empty one
    const lastFilledIndex = digits.length - 1;
    const targetIndex = Math.min(lastFilledIndex + 1, 5);
    const targetInput = document.getElementById(`code-${targetIndex}`);
    if (targetInput) targetInput.focus();
  };

  const handlePatientSubmit = async (e) => {
    e.preventDefault();

    const fullCode = codeDigits.join('');

    if (!fullCode || fullCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setError('');

    try {
      // Use UserService.loginUser which handles both validation and status checking
      // The verify_user_login SQL function automatically detects 6-digit codes
      // and checks patient status (active/discharged)
      const result = await userService.loginUser(fullCode, 'dummy_password');

      if (!result.success || !result.user) {
        setError('Invalid access code. Please check and try again.');
        return;
      }

      console.log('🏥 Patient logged in with access code');

      // Get the actual patient ID from the patients table
      const patientResult = await patientService.verifyAccessCode(fullCode);
      const patientId = patientResult.success ? patientResult.patientId : result.user.id;

      // Store patient info
      localStorage.setItem('user', JSON.stringify({
        name: result.user.name || patientResult.patientName || `Patient ${fullCode.slice(-3)}`,
        isAuthenticated: true,
        userId: result.user.id,
        userType: 'patient',
        role: 'patient',
        accessCode: fullCode
      }));

      // Store the correct patient ID for exercise assignments
      localStorage.setItem('patientId', patientId);
      console.log('📌 LOGIN: Stored patient ID for assignments:', patientId);
      console.log('📌 LOGIN: Patient details:', {
        accessCode: fullCode,
        patientId: patientId,
        patientName: patientResult.patientName,
        fromUserResult: result.user.id,
        fromPatientResult: patientResult.patientId
      });

      // Track login
      trackLogin(`Patient-${fullCode}`);

      // Trigger login event
      window.dispatchEvent(new Event('userLogin'));

      // Always go to patient dashboard
      navigate('/dashboard/patient');

    } catch (error) {
      console.error('Access code error:', error);

      // Check the error message to provide appropriate feedback
      if (error.message && error.message.includes('deactivated')) {
        setError('This access code has been deactivated. Please contact your clinician.');
      } else if (error.message && error.message.includes('Invalid or deactivated')) {
        // This could be either invalid or deactivated
        // Let's check with PatientService for more specific error
        try {
          const { default: patientService } = await import('../../services/PatientService');
          const checkResult = await patientService.verifyAccessCode(fullCode);

          if (!checkResult.success && checkResult.message && checkResult.message.includes('deactivated')) {
            setError('This access code has been deactivated. Please contact your clinician.');
          } else {
            setError('Invalid access code. Please check and try again.');
          }
        } catch {
          setError('Invalid access code. Please check and try again.');
        }
      } else {
        setError('Invalid access code. Please check and try again.');
      }
    }
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();

    // For login, use email; for signup, need both username and email
    if (isSignup) {
      if (!name) {
        setError('Please enter your username');
        return;
      }

      if (name.length < 2) {
        setError('Username must be at least 2 characters long');
        return;
      }
    } else {
      // Login uses email
      if (!email) {
        setError('Please enter your email');
        return;
      }

      // Basic email validation for login
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    if (password.length < 4) {
      setError('Password must be at least 4 characters long');
      return;
    }

    // Additional validation for signup
    if (isSignup) {
      if (!email) {
        setError('Please enter your email');
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address');
        return;
      }

      if (!confirmPassword) {
        setError('Please confirm your password');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }
    
    setError('');
    
    try {
      let result;
      
      if (isSignup) {
        // Register new user with email
        result = await userService.registerUser(name, password, 'clinician', email);
        console.log('👤 User registered:', result);
      } else {
        // Login user with email
        result = await userService.loginUserWithEmail(email, password);
        console.log('👤 User logged in:', result);
      }
      
      // Store user info
      const userType = result.user?.userType || result.user?.user_type || 'clinician';
      const userRole = result.user?.role || userType;
      const displayName = result.user?.name || name || email.split('@')[0];

      localStorage.setItem('user', JSON.stringify({
        name: displayName,
        email: result.user?.email || email,
        isAuthenticated: true,
        userId: result.userId || result.user?.id,
        userType: userType,
        role: userRole
      }));
      
      // Track login
      trackLogin(displayName);
      
      // Trigger login event
      window.dispatchEvent(new Event('userLogin'));
      
      // Redirect based on role
      if (userRole === 'superadmin' || userRole === 'admin') {
        navigate('/dashboard/superadmin');
      } else if (userRole === 'clinician' || userRole === 'therapist') {
        navigate('/dashboard/clinician');
      } else {
        navigate('/dashboard/patient');
      }
      
    } catch (error) {
      console.error('Authentication error:', error);
      setError(error.message || (isSignup ? 'Registration failed. Please try again.' : 'Invalid email or password'));
    }
  };

  const toggleMode = () => {
    setIsPatientMode(!isPatientMode);
    setError('');
    setAccessCode('');
    setCodeDigits(['', '', '', '', '', '']);
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setIsSignup(false);
  };

  const toggleSignup = () => {
    setIsSignup(!isSignup);
    setError('');
    setEmail('');
    setConfirmPassword('');
  };

  return (
    <div className={styles.welcomeContainer}>
      <div className={styles.welcomeContent}>
        <div className={styles.logoContainer}>
          <img src="/auth/logo.svg" alt="Logo" className={styles.welcomeLogo} />
          <h1 className={styles.welcomeTitle}>Welcome</h1>
        </div>

        {/* Mode Indicator - Show current mode clearly */}
        <div style={{
          border: isPatientMode ? '1px solid #10b981' : '1px solid #6d4f79',
          color: isPatientMode ? '#10b981' : '#6d4f79',
          padding: '6px 14px',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '500',
          letterSpacing: '0.5px',
          marginBottom: '24px',
          textAlign: 'center',
          background: 'transparent',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}>
          {isPatientMode ? 'PATIENT LOGIN' : 'STAFF LOGIN'}
        </div>

        {isPatientMode ? (
          // PATIENT MODE - Only 6-digit access code
          <>
            <p className={styles.welcomeSubtitle}>
              Enter your 6-digit access code
            </p>
            
            <form onSubmit={handlePatientSubmit} className={styles.welcomeForm}>
              <div className="codeInputContainer" style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
                marginBottom: '20px'
              }}>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    id={`code-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={codeDigits[index]}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={styles.codeInput}
                    style={{
                      width: '50px',
                      height: '60px',
                      fontSize: '24px',
                      textAlign: 'center',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#6d4f79';
                      e.target.style.backgroundColor = '#f9fafb';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.backgroundColor = '#fff';
                    }}
                    autoComplete="off"
                  />
                ))}
              </div>
              
              {error && (
                <p className={styles.errorMessage}>{error}</p>
              )}
              
              <button type="submit" id="patient-enter-button" style={{
                background: '#6d4f79',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '400',
                transition: 'all 0.2s ease',
                fontFamily: '"Scope One", serif',
                letterSpacing: '0.02em',
                lineHeight: '20px',
                width: 'auto',
                minWidth: '120px',
                marginTop: '16px'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#5a3f66';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#6d4f79';
              }}
              >
                Enter
              </button>
            </form>
            
            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <p style={{ color: '#666', marginBottom: '10px', fontFamily: '"Scope One", serif', fontSize: '15px' }}>
                Are you a clinician or staff member?
              </p>
              <button
                onClick={toggleMode}
                style={{
                  background: 'transparent',
                  color: '#6d4f79',
                  border: '1px solid #6d4f79',
                  padding: '10px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '400',
                  transition: 'all 0.2s ease',
                  fontFamily: '"Scope One", serif',
                  letterSpacing: '0.02em',
                  lineHeight: '20px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#6d4f79';
                  e.target.style.color = 'white';
                  e.target.style.borderColor = '#6d4f79';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#6d4f79';
                  e.target.style.borderColor = '#6d4f79';
                }}
              >
                Switch to Staff Login
              </button>
            </div>
          </>
        ) : (
          // STAFF MODE - Email/username and password
          <>
            <p className={styles.welcomeSubtitle}>
              {isSignup ? 'Create your account' : 'Staff Login'}
            </p>
            
            <form onSubmit={handleStaffSubmit} className={styles.welcomeForm}>
              {isSignup && (
                <input
                  id="name"
                  type="text"
                  className={styles.welcomeInput}
                  placeholder="Username"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="username"
                />
              )}

              <input
                id="email"
                type="email"
                className={styles.welcomeInput}
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />

              <input
                id="password"
                type="password"
                className={styles.welcomeInput}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isSignup ? "new-password" : "current-password"}
              />

              {isSignup && (
                <input
                  id="confirmPassword"
                  type="password"
                  className={styles.welcomeInput}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              )}
              
              {error && (
                <p className={styles.errorMessage}>{error}</p>
              )}
              
              <button type="submit" style={{
                background: '#6d4f79',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '400',
                transition: 'all 0.2s ease',
                fontFamily: '"Scope One", serif',
                letterSpacing: '0.02em',
                lineHeight: '20px',
                width: 'auto',
                minWidth: '120px',
                marginTop: '16px'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#5a3f66';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#6d4f79';
              }}
              >
                {isSignup ? 'Sign up' : 'Login'}
              </button>
            </form>
            
            <p className={styles.modeSwitch} style={{ fontFamily: '"Scope One", serif', fontSize: '15px' }}>
              {isSignup ? (
                <>
                  Already have an account?{' '}
                  <button onClick={toggleSignup} className={styles.linkButton} style={{ fontFamily: '"Scope One", serif', fontSize: '15px' }}>
                    Login
                  </button>
                </>
              ) : (
                <>
                  Don't have an account?{' '}
                  <button onClick={toggleSignup} className={styles.linkButton} style={{ fontFamily: '"Scope One", serif', fontSize: '15px' }}>
                    Sign up
                  </button>
                </>
              )}
            </p>
            
            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <p style={{ color: '#666', marginBottom: '10px', fontFamily: '"Scope One", serif', fontSize: '15px' }}>
                Are you a patient with an access code?
              </p>
              <button
                onClick={toggleMode}
                style={{
                  background: 'transparent',
                  color: '#10b981',
                  border: '1px solid #10b981',
                  padding: '10px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '400',
                  transition: 'all 0.2s ease',
                  fontFamily: '"Scope One", serif',
                  letterSpacing: '0.02em',
                  lineHeight: '20px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#10b981';
                  e.target.style.color = 'white';
                  e.target.style.borderColor = '#10b981';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#10b981';
                  e.target.style.borderColor = '#10b981';
                }}
              >
                Switch to Patient Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailScreen;